import React, { useMemo, useState } from 'react';

function uniqueGroups(accounts) {
  return [...new Set(
    accounts
      .map((acc) => acc.group)
      .filter((g) => g != null && String(g).trim() !== '')
      .map((g) => String(g).trim())
  )].sort();
}

function AccountPicker({
  title,
  accounts,
  selectedIds,
  onChange,
  required = false,
  selectableFilter = () => true,
}) {
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const groups = useMemo(() => uniqueGroups(accounts), [accounts]);

  const selectable = accounts.filter(selectableFilter);
  const allSelected = selectable.length > 0
    && selectable.every((acc) => selectedIds.includes(acc._id));

  const toggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(selectable.map((acc) => acc._id));
    }
  };

  const toggleGroup = (groupName) => {
    const groupAccounts = accounts.filter((acc) => {
      if (!acc.group || String(acc.group).trim() !== groupName) return false;
      return selectableFilter(acc);
    });
    if (!groupAccounts.length) return;
    const groupIds = groupAccounts.map((acc) => acc._id);
    const everySelected = groupIds.every((id) => selectedIds.includes(id));
    if (everySelected) {
      onChange(selectedIds.filter((id) => !groupIds.includes(id)));
    } else {
      onChange([...new Set([...selectedIds, ...groupIds])]);
    }
  };

  const toggleOne = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const toggleExpand = (groupName) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {title}
          {required && <span className="text-red-500 ml-0.5">*</span>}
          {' '}
          <span className="font-normal text-gray-500">
            ({selectedIds.length} selected)
          </span>
        </div>
        <button
          type="button"
          onClick={toggleAll}
          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      </div>
      <div className="max-h-56 overflow-auto border rounded-lg p-2 dark:border-gray-700 space-y-1">
        {accounts.length === 0 ? (
          <p className="text-sm text-gray-500 p-2">No accounts available.</p>
        ) : (
          <>
            {groups.length > 0 && (
              <div className="mb-2 pb-2 border-b border-gray-200 dark:border-gray-700 space-y-1">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">
                  Groups
                </div>
                {groups.map((groupName) => {
                  const groupAccounts = accounts.filter(
                    (acc) => acc.group && String(acc.group).trim() === groupName
                  );
                  const selectableInGroup = groupAccounts.filter(selectableFilter);
                  const groupIds = selectableInGroup.map((acc) => acc._id);
                  const groupAllSelected = groupIds.length > 0
                    && groupIds.every((id) => selectedIds.includes(id));
                  const expanded = expandedGroups.has(groupName);

                  return (
                    <div key={groupName} className="rounded-md bg-gray-50 dark:bg-gray-800/60">
                      <div className="flex items-center gap-2 px-2 py-1">
                        <button
                          type="button"
                          onClick={() => toggleExpand(groupName)}
                          className="text-xs text-gray-500 w-4"
                        >
                          {expanded ? '▼' : '▶'}
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleGroup(groupName)}
                          className={`flex-1 text-left text-sm py-0.5 ${
                            groupAllSelected
                              ? 'text-indigo-600 dark:text-indigo-400 font-medium'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {groupName}
                          <span className="text-gray-400 font-normal ml-1">
                            ({selectableInGroup.length})
                          </span>
                        </button>
                      </div>
                      {expanded && (
                        <div className="pl-6 pb-1 space-y-0.5">
                          {groupAccounts.map((acc) => {
                            const disabled = !selectableFilter(acc);
                            return (
                              <label
                                key={acc._id}
                                className={`flex items-center gap-2 text-sm px-1 ${
                                  disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedIds.includes(acc._id)}
                                  disabled={disabled}
                                  onChange={() => !disabled && toggleOne(acc._id)}
                                />
                                @{acc.username}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="space-y-0.5">
              {accounts
                .filter((acc) => !acc.group || String(acc.group).trim() === '')
                .map((acc) => {
                  const disabled = !selectableFilter(acc);
                  return (
                    <label
                      key={acc._id}
                      className={`flex items-center gap-2 text-sm px-1 ${
                        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(acc._id)}
                        disabled={disabled}
                        onChange={() => !disabled && toggleOne(acc._id)}
                      />
                      @{acc.username}
                    </label>
                  );
                })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


export default AccountPicker;
